import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription, SubscriptionAPI } from 'dva';
import { DvaReducer } from './shared';
import { AVATAR_URL_PREFIX } from '@/constants';
import { ConnectState } from '@/models/connect';
import { FServiceAPI, FUtil } from '@freelog/tools-lib';
import { defaultResourceTypeCodesFilterAll } from '@/models/shared/resourceListHelpers';
import { listStateAndListMore } from '@/components/FListFooter';
import dayjs from 'dayjs';

export interface ResourceCollectPageModelState {
  resourceTypeCodes: {
    value: string;
    label: string;
    values: string[];
    labels: string[];
  };
  resourceStatus: 0 | 1 | 2 | 4 | '#';
  inputText: string;
  inputText_search: string;
  totalNum: number;

  resource_List: {
    id: string;
    cover: string;
    name: string;
    title: string;
    version: string;
    policy: string[];
    type: string[];
    status: 0 | 1;
    updateDate: string;
    username: string;
    useAvatar: string;
    isChoice: boolean;
    collection?: {
      status: 'finish' | 'unFinish';
      count: number;
    } | null;
  }[];
  resource_ListState: 'loading' | 'noData' | 'noSearchResult' | 'loaded';
  resource_ListMore: 'loading' | 'andMore' | 'noMore';
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'resourceCollectPage/change';
  payload: Partial<ResourceCollectPageModelState>;
}

export interface OnMountAction extends AnyAction {
  type: 'resourceCollectPage/onMount';
}

export interface OnUnmountAction extends AnyAction {
  type: 'resourceCollectPage/onUnmount';
}

export interface InitModelStatesAction extends AnyAction {
  type: 'resourceCollectPage/initModelStates';
}

export interface FetchDataSourceAction extends AnyAction {
  type: 'resourceCollectPage/fetchDataSource' | 'fetchDataSource';
  payload: {
    restart: boolean;
  };
}

export interface OnChangeResourceTypeAction extends AnyAction {
  type: 'resourceCollectPage/onChangeResourceType';
  payload: {
    value: ResourceCollectPageModelState['resourceTypeCodes'];
  };
}

export interface OnChangeStatusAction extends AnyAction {
  type: 'resourceCollectPage/onChangeStatus';
  payload: {
    value: 0 | 1 | 2 | 4 | '#';
  };
}

export interface OnChangeKeywordsAction extends AnyAction {
  type: 'resourceCollectPage/onChangeKeywords';
  payload: {
    value: string;
  };
}

export interface OnAwaited_KeywordsChange_Action extends AnyAction {
  type: 'resourceCollectPage/onAwaited_KeywordsChange';
}

export interface OnClickLoadingMordAction extends AnyAction {
  type: 'resourceCollectPage/onClickLoadingMord';
}

export interface OnBoomJuiceAction extends AnyAction {
  type: 'resourceCollectPage/onBoomJuice';
  payload: string;
}

export interface ResourceCollectModelType {
  namespace: 'resourceCollectPage';
  state: ResourceCollectPageModelState;
  effects: {
    onMount: (action: OnMountAction, effects: EffectsCommandMap) => void;
    onUnmount: (action: OnUnmountAction, effects: EffectsCommandMap) => void;
    initModelStates: (
      action: InitModelStatesAction,
      effects: EffectsCommandMap,
    ) => void;
    // changeStates: (action: ChangeStatesAction, effects: EffectsCommandMap) => void;
    fetchDataSource: (
      action: FetchDataSourceAction,
      effects: EffectsCommandMap,
    ) => void;
    onChangeResourceType: (
      action: OnChangeResourceTypeAction,
      effects: EffectsCommandMap,
    ) => void;
    onChangeStatus: (
      action: OnChangeStatusAction,
      effects: EffectsCommandMap,
    ) => void;
    onChangeKeywords: (
      action: OnChangeKeywordsAction,
      effects: EffectsCommandMap,
    ) => void;
    onAwaited_KeywordsChange: (
      action: OnAwaited_KeywordsChange_Action,
      effects: EffectsCommandMap,
    ) => void;
    onClickLoadingMord: (
      action: OnClickLoadingMordAction,
      effects: EffectsCommandMap,
    ) => void;
    onBoomJuice: (
      action: OnBoomJuiceAction,
      effects: EffectsCommandMap,
    ) => void;
  };
  reducers: {
    change: DvaReducer<ResourceCollectPageModelState, ChangeAction>;
  };
  subscriptions: { setup: Subscription };
}

const initStates: ResourceCollectPageModelState = {
  resourceTypeCodes: defaultResourceTypeCodesFilterAll(),
  resourceStatus: '#',
  inputText: '',
  inputText_search: '',
  // dataSource: [],
  // pageSize: 20,
  totalNum: -1,
  resource_List: [],
  resource_ListState: 'loading',
  resource_ListMore: 'loading',
};

const Model: ResourceCollectModelType = {
  namespace: 'resourceCollectPage',

  state: initStates,

  effects: {
    *onMount({}: OnMountAction, { put }: EffectsCommandMap) {
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    *onUnmount({}: OnUnmountAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },
    *initModelStates({}: InitModelStatesAction, { put }: EffectsCommandMap) {
      // console.log('InitModelStatesAction#@#@#@#@##@#');
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },
    *fetchDataSource(
      { payload }: FetchDataSourceAction,
      { call, put, select }: EffectsCommandMap,
    ) {
      // console.log('FetchDataSourceAction23423434');
      const { resourceCollectPage }: ConnectState = yield select(
        ({ resourceCollectPage }: ConnectState) => ({
          resourceCollectPage,
        }),
      );

      let resource_List: ResourceCollectPageModelState['resource_List'] = [];
      if (!payload) {
        resource_List = resourceCollectPage.resource_List;
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            // resource_ListState: 'loading',
            resource_ListMore: 'loading',
          },
        });
      }

      const resourceTypes: string[] =
        resourceCollectPage.resourceTypeCodes.value.split('#');
      let resourceTypeCode: string = resourceTypes[0];
      let resourceTypeCategory: 1 | 2 = 1;
      let resourceType: string = '';

      if (
        resourceTypes.length > 1 &&
        resourceTypes[resourceTypes.length - 1] === 'other'
        // && resourceTypes.at(-1) === 'other'
      ) {
        resourceTypeCode = resourceTypes[0];
        resourceTypeCategory = 2;
      }

      if (
        resourceTypes.length > 1 &&
        resourceTypes[resourceTypes.length - 1] === 'all'
        // && resourceTypes.at(-1) === 'all'
      ) {
        resourceType =
          resourceCollectPage.resourceTypeCodes.labels[
            resourceCollectPage.resourceTypeCodes.labels.length - 2
          ];
      }

      const params: Parameters<
        typeof FServiceAPI.Collection.collectionResources
      >[0] = {
        skip: resource_List.length,
        limit: FUtil.Predefined.pageSize,
        keywords: resourceCollectPage.inputText || undefined,
        resourceType: resourceType || undefined,
        resourceTypeCode:
          resourceType === '' ? resourceTypeCode || undefined : undefined,
        resourceTypeCategory:
          resourceType === '' ? resourceTypeCategory : undefined,
        resourceStatus:
          resourceCollectPage.resourceStatus === '#'
            ? undefined
            : (resourceCollectPage.resourceStatus as 0),
      };

      const {
        ret,
        errCode,
        data: data_collection,
      }: {
        ret: number;
        errCode: number;
        data: {
          limit: number;
          skip: number;
          totalItem: number;
          dataList: {
            resourceId: string;
          }[];
        };
      } = yield call(FServiceAPI.Collection.collectionResources, params);
      // console.log(data_collection, ' data_collection data3290joisdf');

      // let data1: any[] = [];

      if (data_collection.dataList.length === 0) {
        const { state, more } = listStateAndListMore({
          list_Length: 0,
          total_Length: data_collection.totalItem,
          has_FilterCriteria:
            resourceCollectPage.inputText !== '' ||
            resourceCollectPage.resourceTypeCodes.value !== '#all' ||
            resourceCollectPage.resourceStatus !== '#',
        });
        yield put<ChangeAction>({
          type: 'change',
          payload: {
            resource_List: [],
            resource_ListState: state,
            resource_ListMore: more,
            totalNum: data_collection.totalItem,
            inputText_search: resourceCollectPage.inputText,
          },
        });
        return;
        // data1 = data2;
      }
      const params1: Parameters<typeof FServiceAPI.Resource.batchInfo>[0] = {
        resourceIds: data_collection.dataList
          .map((d) => d.resourceId)
          .join(','),
      };

      const {
        data: data_batchInfo,
      }: {
        ret: number;
        errCode: number;
        data: {
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceType: string[];
          latestVersion: string;
          coverImages: string[];
          status: 0 | 1;
          policies: {
            policyId: string;
            policyName: string;
            status: 0 | 1;
          }[];
          userId: number;
          updateDate: string;
          username: string;
          useAvatar: string;
          operationType: 0 | 1 | 2 | 3 | 4 | 5;
          serializeStatus: 0 | 1;
          subjectType: 1 | 4;
          resourceVersions: {
            versionId: string;
          }[];
        }[];
      } = yield call(FServiceAPI.Resource.batchInfo, params1);
      // console.log(data_batchInfo, 'data_resourceList data1w09ejflk23');

      const params2: Parameters<typeof FServiceAPI.Resource.getVersionList>[0] =
        {
          versionIds: data_batchInfo
            .filter((bi) => {
              return bi.subjectType === 4;
            })
            .map((bi) => {
              return bi.resourceVersions[0].versionId;
            })
            .join(','),
        };
      const {
        data: data_versionList,
      }: {
        data: {
          resourceId: string;
          catalogueProperty: { collection_item_count: number };
        }[];
      } = yield call(FServiceAPI.Resource.getVersionList, params2);
      // console.log(data_versionList, 'data_versionList sdifjsl;dkfjlkjdlkjfl');
      const finalList: ResourceCollectPageModelState['resource_List'] = [
        ...resource_List,
        ...data_collection.dataList.map<
          ResourceCollectPageModelState['resource_List'][number]
        >((i) => {
          const resourceInfo = data_batchInfo.find((dbi) => {
            return dbi.resourceId === i.resourceId;
          });
          const resourceVersionInfo = data_versionList.find((vl) => {
            return vl.resourceId === i.resourceId;
          });

          if (!resourceInfo) {
            return {
              id: i.resourceId,
              cover: '',
              name: '',
              title: '',
              version: '',
              policy: [],
              type: [],
              status: 0,
              updateDate: '',
              username: '',
              useAvatar: '',
              isChoice: false,
              collection: null,
            };
          }

          return {
            id: i.resourceId,
            cover:
              resourceInfo.coverImages.length > 0
                ? resourceInfo.coverImages[0]
                : '',
            name: resourceInfo.resourceName,
            title: resourceInfo.resourceTitle,
            version: resourceInfo.latestVersion,
            policy: resourceInfo.policies
              .filter((l: any) => {
                return l.status === 1;
              })
              .map((l: any) => l.policyName),
            type: resourceInfo.resourceType,
            status: resourceInfo.status,
            updateDate: dayjs(resourceInfo.updateDate).format(FUtil.Predefined.momentDateFormat),
            username: resourceInfo.username,
            useAvatar: `${AVATAR_URL_PREFIX}${resourceInfo.userId}`,
            isChoice: resourceInfo.operationType === 5,
            collection:
              resourceInfo.subjectType === 4 && !!resourceVersionInfo
                ? {
                    status:
                      resourceInfo.serializeStatus === 1
                        ? 'finish'
                        : 'unFinish',
                    count:
                      resourceVersionInfo.catalogueProperty
                        .collection_item_count,
                  }
                : null,
          };
        }),
      ];

      const { state, more } = listStateAndListMore({
        list_Length: finalList.length,
        total_Length: data_collection.totalItem,
        has_FilterCriteria:
          resourceCollectPage.inputText !== '' ||
          resourceCollectPage.resourceTypeCodes.value !== '#all' ||
          resourceCollectPage.resourceStatus !== '#',
      });

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resource_List: finalList,
          resource_ListState: state,
          resource_ListMore: more,
          totalNum: data_collection.totalItem,
          inputText_search: resourceCollectPage.inputText,
        },
      });
    },
    *onChangeResourceType(
      { payload }: OnChangeResourceTypeAction,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceTypeCodes: payload.value,
        },
      });

      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    *onChangeStatus(
      { payload }: OnChangeStatusAction,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceStatus: payload.value,
        },
      });

      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    *onChangeKeywords(
      { payload }: OnChangeKeywordsAction,
      { put }: EffectsCommandMap,
    ) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          inputText: payload.value,
        },
      });

      // yield put<FetchDataSourceAction>({
      //   type: 'fetchDataSource',
      //   payload: {
      //     restart: true,
      //   },
      // });
    },
    *onAwaited_KeywordsChange(
      {}: OnAwaited_KeywordsChange_Action,
      { put }: EffectsCommandMap,
    ) {
      // console.log('dddddDDDDDDDfsdf sdfsdf ******');
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: true,
        },
      });
    },
    *onClickLoadingMord(
      {}: OnClickLoadingMordAction,
      { put }: EffectsCommandMap,
    ) {
      yield put<FetchDataSourceAction>({
        type: 'fetchDataSource',
        payload: {
          restart: false,
        },
      });
    },
    *onBoomJuice(
      { payload }: OnBoomJuiceAction,
      { call, put, select }: EffectsCommandMap,
    ) {
      const params: Parameters<
        typeof FServiceAPI.Collection.deleteCollectResource
      >[0] = {
        resourceId: payload,
      };
      yield call(FServiceAPI.Collection.deleteCollectResource, params);
      const { resourceCollectPage }: ConnectState = yield select(
        ({ resourceCollectPage }: ConnectState) => ({
          resourceCollectPage,
        }),
      );
      const resourceTypes: Array<string | number> =
        resourceCollectPage.resourceTypeCodes.labels.filter((rt) => {
          return rt !== '全部';
        });

      const finalList = resourceCollectPage.resource_List.filter(
        (ds) => ds.id !== payload,
      );
      const finalTotalNum = resourceCollectPage.totalNum - 1;

      const { state, more } = listStateAndListMore({
        list_Length: finalList.length,
        total_Length: finalTotalNum,
        has_FilterCriteria:
          resourceCollectPage.inputText !== '' ||
          resourceCollectPage.resourceTypeCodes.value !== '#all' ||
          resourceCollectPage.resourceStatus !== '#',
      });

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          // resource_List: resourceCollectPage.resource_List.filter((ds) => ds.id !== payload),
          // totalNum: resourceCollectPage.totalNum - 1,
          resource_List: finalList,
          resource_ListState: state,
          resource_ListMore: more,
          totalNum: finalTotalNum,
          inputText_search: resourceCollectPage.inputText,
        },
      });
    },
  },

  reducers: {
    change(
      state: ResourceCollectPageModelState,
      { payload }: ChangeAction,
    ): ResourceCollectPageModelState {
      return {
        ...state,
        ...payload,
      };
    },
  },

  subscriptions: {
    setup({ dispatch, history }: SubscriptionAPI) {
      // history.listen((listener) => {
      //   if (listener.pathname === '/resource/collect') {
      //     dispatch<FetchDataSourceAction>({
      //       type: 'fetchDataSource',
      //     });
      //   }
      // });
    },
  },
};

export default Model;
